import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authAPI, setLastLoginTime, isInLoginGracePeriod } from '../services/api';
import { verify2FACodeForLogin } from '../services/twoFactorApi';

export type UserRole = 'admin' | 'supervisor' | 'employee' | 'client';

export interface User {
  id: string;
  company_id?: string;
  email: string;
  name: string;
  role: UserRole;
  is_active: boolean;
  phone?: string;
  document?: string;
  birth_date?: string;
  email_verified?: boolean;
  last_login_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, companyId?: string) => Promise<{ success: boolean; requires2FA?: boolean; requiresSetup?: boolean; userId?: string; error?: string }>;
  verify2FA: (userId: string, code: string) => Promise<boolean>;
  register: (email: string, password: string, role: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  isEmployee: boolean;
  isClient: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode; value?: Partial<AuthContextType> }> = ({
  children,
  value: overrideValue,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  // Verificar token na inicialização (apenas uma vez)
  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');

      if (savedToken) {
        try {
          // Verificar novamente se o token ainda está disponível antes de fazer requisição
          // Isso evita race conditions onde o token foi removido entre a verificação e a requisição
          const currentToken = localStorage.getItem('token');
          if (!currentToken || currentToken !== savedToken) {
            console.warn('[AuthContext] Token foi removido antes da verificação de perfil');
            if (isMounted) {
              setToken(null);
              setUser(null);
              setIsLoading(false);
            }
            return;
          }

          const response = await authAPI.get('/users/profile');

          // Adicionar campo name se não existir (usar email como fallback)
          const userData = {
            ...response.data,
            name: (response.data && response.data.name) || (response.data && response.data.email),
          };

          if (isMounted) {
            setUser(userData);
            setToken(savedToken);
          }
        } catch (error: any) {
          // Token inválido ou expirado, limpar
          if (isMounted) {
            localStorage.removeItem('token');
            localStorage.removeItem('default_company_id');
            setToken(null);
            setUser(null);
          }
        }
      } else {
        // Sem token, garantir que user está null
        if (isMounted) {
          setUser(null);
          setToken(null);
        }
      }
      
      if (isMounted) {
        setIsLoading(false);
      }
    };

    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Ouvir eventos de token inválido de outros interceptores
  useEffect(() => {
    const handleTokenInvalid = () => {
      if (isInLoginGracePeriod()) {
        console.warn('[AuthContext] Token invalidado durante período de graça, ignorando logout');
        return;
      }
      setToken(null);
      setUser(null);
    };

    window.addEventListener('auth:token-invalid', handleTokenInvalid);
    
    return () => {
      window.removeEventListener('auth:token-invalid', handleTokenInvalid);
    };
  }, []);

  const login = async (email: string, password: string, companyId?: string): Promise<{ success: boolean; requires2FA?: boolean; requiresSetup?: boolean; userId?: string; error?: string }> => {
    try {
      // Se companyId não for fornecido, tentar buscar empresa padrão
      let finalCompanyId = companyId;
      if (!finalCompanyId) {
        try {
          // Tentar buscar do localStorage primeiro
          const savedCompanyId = localStorage.getItem('default_company_id');
          if (savedCompanyId) {
            finalCompanyId = savedCompanyId;
          } else {
            // Tentar buscar empresa padrão via API (pode falhar se endpoint não existir)
            try {
              const companyResponse = await authAPI.get('/auth/default-company');
              finalCompanyId = companyResponse.data.company_id;
              // Salvar no localStorage para próximas requisições
              if (finalCompanyId) {
                localStorage.setItem('default_company_id', finalCompanyId);
              }
            } catch (apiError: any) {
              // Se endpoint não existir (404) ou der erro (500), fazer login sem company_id
              // O backend vai usar empresa padrão automaticamente
              if (apiError.response?.status === 404) {
                console.warn('Endpoint default-company não disponível (404), fazendo login sem company_id');
              } else if (apiError.response?.status === 500) {
                console.warn('Endpoint default-company retornou erro 500, fazendo login sem company_id');
              } else {
                console.warn('Erro ao buscar empresa padrão, fazendo login sem company_id:', apiError.message);
              }
              finalCompanyId = undefined;
            }
          }
        } catch (e) {
          console.error('Erro ao buscar empresa padrão:', e);
          // Continuar sem company_id - backend vai usar padrão
          finalCompanyId = undefined;
        }
      }

      // Validar que email e password não estão vazios
      if (!email || !email.trim()) {
        return { 
          success: false,
          error: 'Email é obrigatório'
        };
      }
      
      if (!password || !password.trim()) {
        return { 
          success: false,
          error: 'Senha é obrigatória'
        };
      }

      // Preparar payload de login
      const loginPayload: any = {
        email: email.trim(),
        password: password.trim(),
      };

      // Incluir company_id apenas se disponível e válido
      // O backend aceita company_id opcional, então podemos enviar ou não
      // Se não enviar, o backend vai usar a empresa padrão automaticamente
      if (finalCompanyId && finalCompanyId.trim() !== '') {
        // Validar se é um UUID válido antes de enviar
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(finalCompanyId)) {
          loginPayload.company_id = finalCompanyId;
        } else {
          console.warn('company_id inválido, fazendo login sem company_id');
        }
      }

      // Log para debug (apenas em desenvolvimento)
      if (import.meta.env.DEV) {
        console.log('[AuthContext] Enviando login payload:', { 
          email: loginPayload.email, 
          password: '***', 
          hasCompanyId: !!loginPayload.company_id 
        });
      }

      const response = await authAPI.post('/auth/login', loginPayload);

      // Verificar se 2FA é necessário (já configurado)
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          userId: response.data.user_id,
        };
      }

      const { token: newToken, user: userData, requires_setup } = response.data;

      // Adicionar campo name se não existir (usar email como fallback)
      const userWithName = {
        ...userData,
        name: (userData && userData.name) || (userData && userData.email),
      };

      localStorage.setItem('token', newToken);
      localStorage.removeItem('menu-permissions');
      setToken(newToken);
      setUser(userWithName);
      setLastLoginTime();

      if (requires_setup) {
        return { success: true, requiresSetup: true };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Erro no login:', error);

      // Melhorar mensagem de erro baseado no status HTTP
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.error || error.response.data?.message;

        if (status === 500) {
          console.error('Erro 500 do servidor:', errorMessage || error.response.data);
          // Erro 500 geralmente indica problema no servidor (ex: empresa padrão não existe)
          return {
            success: false,
            error: 'Erro interno do servidor. Por favor, entre em contato com o administrador.'
          };
        } else if (status === 401) {
          return {
            success: false,
            error: 'Email ou senha incorretos'
          };
        } else if (status === 400) {
          return {
            success: false,
            error: errorMessage || 'Dados inválidos. Verifique os campos e tente novamente.'
          };
        } else if (status === 403) {
          return {
            success: false,
            error: errorMessage || 'Acesso negado. Verifique suas permissões.'
          };
        }
      }

      return {
        success: false,
        error: 'Erro de conexão. Verifique se os serviços estão rodando.'
      };
    }
  };

  const verify2FA = async (userId: string, code: string): Promise<boolean> => {
    try {
      const response = await verify2FACodeForLogin(userId, code);
      const { token: newToken, user: userData } = response;

      // Adicionar campo name se não existir (usar email como fallback)
      const userWithName = {
        ...userData,
        name: (userData && userData.name) || (userData && userData.email),
      };

      localStorage.setItem('token', newToken);
      localStorage.removeItem('menu-permissions');
      setToken(newToken);
      setUser(userWithName);

      // Registrar tempo do login para período de graça nos interceptores
      setLastLoginTime();

      return true;
    } catch (error) {
      console.error('Erro ao verificar código 2FA:', error);
      return false;
    }
  };

  const register = async (email: string, password: string, role: string): Promise<boolean> => {
    try {
      const response = await authAPI.post('/register', {
        email,
        password,
        role,
      });

      // Após registro, fazer login automaticamente
      const loginResult = await login(email, password);
      return loginResult.success;
    } catch (error) {
      console.error('Erro no registro:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Limpar permissões do menu para evitar problemas de cache
    localStorage.removeItem('menu-permissions');
    setToken(null);
    setUser(null);
  };

  // Verificar se tem um role específico
  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  // Matriz de permissões por role
  const rolePermissions: Record<UserRole, string[]> = {
    admin: [
      'users:create',
      'users:read',
      'users:update',
      'users:delete',
      'users:manage_roles',
      'clients:create',
      'clients:read',
      'clients:read_all',
      'clients:update',
      'clients:delete',
      'properties:create',
      'properties:read',
      'properties:read_all',
      'properties:update',
      'properties:delete',
      'appointments:create',
      'appointments:read',
      'appointments:read_all',
      'appointments:update',
      'appointments:cancel',
      'appointments:approve',
      'services:start',
      'services:complete',
      'services:read',
      'payments:read',
      'payments:read_all',
      'invoices:create',
      'prices:update',
      'reports:operational',
      'reports:financial',
      'reports:analytics',
      'system:config',
      'audit:read',
    ],
    supervisor: [
      'clients:read',
      'clients:read_all',
      'properties:read',
      'properties:read_all',
      'appointments:create',
      'appointments:read',
      'appointments:read_all',
      'appointments:update',
      'appointments:cancel',
      'appointments:approve',
      'services:read',
      'reports:operational',
    ],
    employee: [
      'appointments:read',
      'appointments:update',
      'services:start',
      'services:complete',
      'services:read',
      'properties:read',
    ],
    client: [
      'properties:create',
      'properties:read',
      'properties:update',
      'appointments:create',
      'appointments:read',
      'appointments:cancel',
      'services:read',
      'payments:read',
    ],
  };

  // Verificar se tem uma permissão específica
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    const permissions = rolePermissions[user.role] || [];
    return permissions.includes(permission);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    verify2FA,
    register,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
    hasRole,
    hasPermission,
    isAdmin: user?.role === 'admin',
    isSupervisor: user?.role === 'supervisor',
    isEmployee: user?.role === 'employee',
    isClient: user?.role === 'client',
  };

  if (overrideValue) {
    const mergedValue: AuthContextType = {
      ...value,
      ...overrideValue,
      user: overrideValue.user ?? value.user,
      token: overrideValue.token ?? value.token,
      isLoading: overrideValue.isLoading ?? value.isLoading,
      isAuthenticated: overrideValue.isAuthenticated ?? value.isAuthenticated,
      hasRole: overrideValue.hasRole ?? value.hasRole,
      hasPermission: overrideValue.hasPermission ?? value.hasPermission,
      isAdmin: overrideValue.isAdmin ?? value.isAdmin,
      isSupervisor: overrideValue.isSupervisor ?? value.isSupervisor,
      isEmployee: overrideValue.isEmployee ?? value.isEmployee,
      isClient: overrideValue.isClient ?? value.isClient,
    } as AuthContextType;

    return <AuthContext.Provider value={mergedValue}>{children}</AuthContext.Provider>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
